/**
 * `main.tsx` — the web app's entry point. `docs/plan.md` T47; `docs/spec.md` §13.1,
 * §13.8, §14.4, §17.2; `docs/design-direction.md` §6.1.
 *
 * `apps/web/index.html` loads this as `<script type="module" src="/src/main.tsx">` —
 * nothing before this file has drawn a pixel of the app itself (only the inline
 * `<style>` that paints the ground colour so the WebView never flashes white).
 *
 * ## Boot order, and why each half of it is shaped the way it is
 *
 * `plan.md` T47 states the order as one chain:
 *
 * > `telegram.ready()` as soon as the shell renders → `expand()` → `claimChrome()` →
 * > `syncViewport()` → `disableVerticalSwipes()` → `enableClosingConfirmation()` →
 * > `detectFonts()` → `perf.probeGrain()` → `POST /api/auth/telegram` → hydrate
 * > stores → render.
 *
 * `skin.initSkin()` joins that chain beside the font and grain probes, and for the same
 * reason: it is fire-and-forget and nothing may wait on it. Note that it is NOT what
 * first applies the skin — `index.html`'s inline script did that before this file was
 * fetched, because a skin applied here would arrive several frames after the ground has
 * already been painted.
 *
 * Read literally that is a contradiction — render is both first ("as soon as the
 * shell renders") and last. It resolves into two different kinds of step:
 *
 *  1. **The synchronous, fire-and-forget half** — every call up to and including the
 *     font and grain probes. None of it may block on network or on each other:
 *     `ready()` in particular must fire in the same tick the shell is painted, or
 *     Telegram's own loading shim sits over an already-drawn UI (the exact failure
 *     the plan calls out by name). `initFonts()` (`lib/fonts.ts`'s own entry-point
 *     call, which is `detectFonts()` plus a live watcher) and `probeGrain()`
 *     (`lib/perf.ts`) are each explicitly "fire-and-forget… because nothing may"
 *     wait on them — least of all the thing that grants Telegram back its own splash.
 *  2. **The one real network step and what depends on it.** `POST /api/auth/telegram`
 *     is a genuine round trip, so "hydrate stores → render" describes what happens
 *     *when it resolves* — the queue store's `load()` — rather than a fourth
 *     synchronous call. The actual React `render()` call already happened in step 1,
 *     against whichever store state existed at that instant (empty, the normal
 *     case); the store update after authentication lands through the normal zustand
 *     subscription and React re-renders reactively. Nothing here calls `render()` a
 *     second time by hand.
 *
 * `getWiring().start()` is not named in the plan's chain at all — its own doc
 * comment is explicit that "the entry point calls `getWiring().start()` once, after
 * the shell has rendered and `telegram.ready()` has hidden the splash", so it is
 * called from this file, in the synchronous half, without waiting on authentication:
 * it only subscribes engine events, Media Session and Telegram's `activated` /
 * `deactivated`, none of which touch the network or start playback on their own —
 * §13.3's "no autoplay before a gesture" rule is instead the whole reason nothing in
 * this file ever calls `wiring.select()` or `wiring.play()`.
 *
 * ## The launch guard
 *
 * `spec.md` §14.4 (the `InlineQueryResultsButton` note) and the general "opened
 * outside the bot's menu button" case both produce a Mini App with **empty**
 * `initData` — no signature, no `user`. `lib/telegram.ts`'s own contract is that
 * `MiniAppInitDataUnsafe` never parses `user` at all — "identity comes from the
 * server's verification of the raw `initData`, never from this object" — so
 * {@link hasTelegramUser} does not reach for a typed field that does not exist; it
 * reads the raw signed query string exactly once, purely to decide whether the
 * exchange is worth *attempting*. It trusts nothing it finds there — a forged
 * `user=` parameter buys nothing, because the server independently re-verifies the
 * whole string's HMAC before it ever issues a token. When it comes back `false`,
 * this file renders {@link LaunchGuard} **instead of** `<AppShell />` and returns
 * before the auth step — no request, authenticated or not, is ever issued from a
 * launch the server could not have accepted anyway (plan.md T47 acceptance 2).
 *
 * ## The service worker
 *
 * `sw.ts` has no `vite-plugin-pwa`-style build step available to it (`vite.config.ts`
 * is out of this task's file list and declares no worker plugin), so it is bundled
 * the way Vite documents for exactly this case: `?worker&url` (declared by
 * `vite/client`'s own ambient types) runs the referenced module through Vite's
 * worker pipeline — TypeScript stripped, its own module graph resolved and bundled —
 * and yields the **built** URL as a plain string, without instantiating a `Worker`.
 * That URL is what gets handed to `navigator.serviceWorker.register()`.
 * Registration itself is gated on `FEATURE_SW` (§13.8: off by default, "reported
 * non-functional in Telegram Mini Apps on iOS"), deferred until the window's `load`
 * event (well after first paint), and its promise is never awaited by anything in
 * this file (plan.md T47 acceptance 6) — every feature of this app works with no
 * service worker at all.
 */

import { StrictMode, useCallback, useState } from 'react';
import type { JSX } from 'react';
import { createRoot } from 'react-dom/client';

import { Shell } from './app/Shell';
import type { RouteMatch, RouteName } from './app/router';
import { DEEP_LINK_TRACK, openDeepLink } from './app/deeplink';
import { buildPath, navigate, Router } from './app/router';
import { StateFrame } from './app/states/Loading';
import { joinUrlFromDetails, SubscribeGate } from './app/states/SubscribeGate';
import { getWiring } from './app/wiring';
import { MiniPlayer } from './components/MiniPlayer';
import type { NavKey } from './components/NavStrip';
import { NavStrip } from './components/NavStrip';
import { cx } from './components/primitives/Rule';
import { initFonts } from './lib/fonts';
import { probeGrain } from './lib/perf';
import { initSkin } from './lib/skin';
import { telegram } from './lib/telegram';
import { selectProgressFraction, usePlayerStore } from './store/player';
import { selectCurrentTrack, useQueueStore } from './store/queue';
import { offerFromLoadResult, useResumeStore } from './store/resume';
import { useSessionStore } from './store/session';

import './styles/index.css';

// The `?worker&url` import documented above — see the module doc comment's "The
// service worker" section for why this specific suffix and not a plain `?url`.
import swUrl from './sw.ts?worker&url';

/* ══ the launch guard ══════════════════════════════════════════════════════════════ */

/**
 * `true` when the raw `initData` carries a parseable `user` object. Reads the raw
 * string directly rather than any typed `initDataUnsafe.user` — see the module doc
 * comment. An empty string (opened outside Telegram, or through the
 * `InlineQueryResultsButton` path of §14.4) always reads as `false`.
 */
export function hasTelegramUser(initData: string): boolean {
  if (initData.length === 0) return false;

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(initData);
  } catch {
    return false;
  }

  const raw = params.get('user');
  if (raw === null || raw.trim().length === 0) return false;

  try {
    const parsed: unknown = JSON.parse(raw);
    return (
      parsed !== null &&
      typeof parsed === 'object' &&
      typeof (parsed as { id?: unknown }).id !== 'undefined'
    );
  } catch {
    return false;
  }
}

/** English catalogue-style identity mark; decorative, hence `aria-hidden`. */
const GUARD_MARK = 'FLASH INDUSTRIES';

/**
 * Sentence case throughout (§4.3, §12.16: tracked Cyrillic caps read as shouting).
 * Says what happened and what to do, exactly the copy rule `ErrorState` documents for
 * every plate in the app.
 */
const GUARD_SENTENCE = 'Плеер открывается только из меню Telegram.';
const GUARD_DETAIL = 'Найдите FLASH INDUSTRIES в меню чата с ботом и откройте оттуда.';

/**
 * Rendered **instead of** the authenticated shell when {@link hasTelegramUser} is
 * `false` — never as a loading placeholder on the way to one. It shares `Shell` (so
 * the ground, the grain and the safe-area geometry are identical to every other
 * screen) but takes no `dock`: there is no player, no queue and no nav strip to
 * offer when there is no session to have any of those things.
 */
export function LaunchGuard(): JSX.Element {
  return (
    <Shell>
      <StateFrame grid="plain">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s3)' }}>
          <span className={cx('t-mono', 't-11')} aria-hidden="true">
            {GUARD_MARK}
          </span>
          <p className={cx('t-ui', 't-17')} data-state-string="guard">
            {GUARD_SENTENCE}
          </p>
          <p className={cx('t-ui', 't-15', 't-clamp-3')}>{GUARD_DETAIL}</p>
        </div>
      </StateFrame>
    </Shell>
  );
}

/* ══ the authenticated shell ═══════════════════════════════════════════════════════ */

/** Which nav-strip tab a route belongs to, for the persistent dock's active mark. */
export function navKeyForRoute(route: RouteName): NavKey {
  switch (route) {
    case 'search':
      return 'search';
    case 'favorites':
    case 'playlist':
    case 'history':
      return 'mine';
    case 'pads':
      return 'pads';
    default:
      return 'crate';
  }
}

/**
 * The one place the mini player, the nav strip and the router meet — `Shell`'s doc
 * comment names all three as slots "other tasks own", and this is the entry point
 * that owns composing them.
 *
 * The dock is omitted entirely on `/now`: Now Playing already shows the full
 * transport, and a second play/pause control docked under it is pure redundancy in a
 * screen that is otherwise built around the rule that any one screen shows exactly
 * one loud object.
 */
function AppShell(): JSX.Element {
  const [route, setRoute] = useState<RouteName>('crate');
  const track = useQueueStore(selectCurrentTrack);
  const isPlaying = usePlayerStore((state) => state.isPlaying);
  const progress = usePlayerStore(selectProgressFraction);

  const onRouteChange = useCallback((match: RouteMatch): void => {
    setRoute(match.route);
  }, []);

  const onToggle = useCallback((): void => {
    void getWiring().toggle();
  }, []);

  const onExpand = useCallback((): void => {
    navigate(buildPath('now'));
  }, []);

  const onOpenQueue = useCallback((): void => {
    navigate(buildPath('queue'));
  }, []);

  const onSelectNav = useCallback((key: NavKey): void => {
    const target: RouteName =
      key === 'search' ? 'search' : key === 'mine' ? 'favorites' : key === 'pads' ? 'pads' : 'crate';
    navigate(buildPath(target));
  }, []);

  const dock =
    route === 'now' ? undefined : (
      <>
        {track === null ? null : (
          <MiniPlayer
            title={track.title}
            performer={track.performer}
            messageId={track.id}
            seed={track.plate.seed}
            playing={isPlaying}
            progress={progress}
            onToggle={onToggle}
            onExpand={onExpand}
            onOpenQueue={onOpenQueue}
          />
        )}
        <NavStrip active={navKeyForRoute(route)} onSelect={onSelectNav} />
      </>
    );

  return (
    <Shell dock={dock}>
      <Router onRouteChange={onRouteChange} />
    </Shell>
  );
}

/* ══ the members-only gate ═════════════════════════════════════════════════════════ */

/**
 * The one place the app decides between "you are in" and "you are not a subscriber".
 *
 * It is a **root switch**, not a screen the router can reach: `403 NOT_SUBSCRIBED`
 * (`spec.md` §9.8) means no token was issued at all, so there is no catalogue to browse
 * behind the message and no route that could be allowed through. Every other failure
 * keeps the normal shell — the screens already render their own `ErrorState`, and
 * replacing the whole app for a dropped connection would throw away the queue.
 *
 * `LaunchGuard` stays a separate, earlier branch: it fires before any request, on a
 * launch that carries no `initData` at all.
 */
export function Root(): JSX.Element {
  const failure = useSessionStore((state) => state.error);
  const busy = useSessionStore((state) => state.status === 'authenticating');

  const onRetry = useCallback((): void => {
    void (async (): Promise<void> => {
      const authenticated = await useSessionStore.getState().authenticate();
      if (authenticated) await afterAuthenticate();
    })();
  }, []);

  if (failure?.code !== 'NOT_SUBSCRIBED') return <AppShell />;

  return (
    <Shell>
      <SubscribeGate joinUrl={joinUrlFromDetails(failure.details)} onRetry={onRetry} busy={busy} />
    </Shell>
  );
}

/* ══ service worker registration ═══════════════════════════════════════════════════ */

/**
 * Vite's own `VITE_`-prefixed client-env convention. The API's `FEATURE_SW`
 * (`apps/api/src/config.ts`) is a **server** flag with no templating path into this
 * static build, so the client reads its own env var; unset (the default in every
 * environment) keeps the shared off-by-default behaviour from `spec.md` §13.8.
 */
const FEATURE_SW = import.meta.env.VITE_FEATURE_SW === 'true';

/**
 * Registers `sw.ts`, deferred past `load` and never awaited by {@link boot}. A
 * rejected registration (Telegram's iOS WebView is the documented case, §13.8) is
 * swallowed: every feature of this app is specified to work with no service worker
 * at all.
 */
function registerServiceWorkerAfterFirstPaint(): void {
  if (!FEATURE_SW) return;
  if (typeof window === 'undefined') return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  const register = (): void => {
    void navigator.serviceWorker.register(swUrl).catch(() => {
      /* Progressive enhancement only — see the doc comment above. */
    });
  };

  if (document.readyState === 'complete') {
    register();
    return;
  }
  window.addEventListener('load', register, { once: true });
}

/* ══ boot ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Runs the whole sequence documented at the top of this file. Exported for a future
 * integration test to drive against injected globals; `apps/web/index.html` triggers
 * it purely by importing this module (see the bottom of the file).
 */
export async function boot(): Promise<void> {
  const container = document.getElementById('root');
  if (container === null) return;

  const guard = !hasTelegramUser(telegram.initData);

  createRoot(container).render(<StrictMode>{guard ? <LaunchGuard /> : <Root />}</StrictMode>);

  // The synchronous, fire-and-forget half of the boot order (see the module doc
  // comment). `ready()` first, unconditionally: the guard screen is still a real
  // Telegram launch (an inline-mode share with no `user`, or a browser tab running
  // the dev shim) and Telegram's own splash must be dismissed either way.
  telegram.ready();
  telegram.expand();
  telegram.claimChrome();
  telegram.syncViewport();
  telegram.disableVerticalSwipes();
  telegram.enableClosingConfirmation();

  void initFonts();
  void probeGrain();
  /*
   * The skin is already on `<html>` — `index.html`'s inline script put it there before
   * this bundle existed, which is what keeps the first painted frame from flashing the
   * previous skin. This call re-applies the same local answer (so the app is still
   * correct in a harness or a webview that stripped the inline script) and then, in its
   * asynchronous half, asks Telegram's `CloudStorage` whether the ACCOUNT has a
   * different one and repaints if so. Fire-and-forget for exactly the reason the two
   * calls above it are: a Mini App may not wait on a round trip to draw.
   */
  void initSkin().then(() => {
    /* The header, background and bottom bar are painted by Telegram, not by our CSS, so
       a skin that arrived from the cloud only reaches them through a re-assert. */
    telegram.claimChrome();
  });
  registerServiceWorkerAfterFirstPaint();

  if (guard) return; // §14.4: no `user`, no request — acceptance 2.

  // Harmless with no session yet: subscribes engine/Telegram/Media-Session listeners
  // and starts nothing playing (§13.3's "no autoplay before a gesture").
  getWiring().start();

  const authenticated = await useSessionStore.getState().authenticate();
  if (authenticated) await afterAuthenticate();
}

/**
 * Everything that depends on there being a session: the queue, the deep link, the resume
 * offer. Split out of {@link boot} because the members-only gate's `Я подписался` button
 * re-runs the exchange and must then continue the boot from exactly this point — a
 * successful late authentication has to leave the app in the same state as an early one.
 */
async function afterAuthenticate(): Promise<void> {
  await useQueueStore.getState().load();

  /*
   * F15, last: act on the payload the launch carried. It has to happen HERE and in
   * this order — after `authenticate()`, because the signed `start_param` arrives on
   * that response (§12.3), and after `load()`, because the deep link makes its track
   * the queue's current item and a later hydrate would overwrite it.
   *
   * `openDeepLink` never rejects and never navigates for a payload it cannot resolve,
   * so no branch is needed: a bad link leaves the user in the crate, which is exactly
   * §12.3's rule.
   */
  const target = await openDeepLink(useSessionStore.getState().startParam);

  /*
   * M6 cell 5, "reload mid-session". `persistence.load()` reconciles all four tiers
   * (§13.7) and `Wiring` exposes it for exactly this call — it was simply never made,
   * so a reload lost both the position and the track.
   *
   * It is an OFFER, not a resume (§13.2): the reload is not a gesture, so nothing is
   * selected or played here. `ResumeBanner` at the top of the crate turns it into one.
   *
   * Skipped when a deep link already chose a track — the user asking for THIS record
   * outranks what they were playing yesterday, and both writing the queue would make
   * the winner depend on the order of two awaits.
   */
  if (target !== DEEP_LINK_TRACK) {
    try {
      const restored = await getWiring().persistence.load();
      useResumeStore.getState().setOffer(offerFromLoadResult(restored));
    } catch {
      // A restore that cannot be read is not a boot failure: the app opens on the
      // crate exactly as it does for a first-ever launch.
    }
  }
}

if (typeof document !== 'undefined') {
  void boot();
}
